/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaCar, FaSearch, FaClipboardList, FaArrowLeft, FaCheckCircle, FaTimesCircle, FaClock, FaTrash, FaFilter } from 'react-icons/fa';
import '../../pages_premium.css';
import '../PropertyRequests.css';
import './CarsList.css';
import { getFullImageUrl } from '../../utils/url';
import AdminPagination from '../../components/common/AdminPagination';

export default function CarRequestsList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const carId = searchParams.get('carId');
    const navigate = useNavigate();

    const [requests, setRequests] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const fetchCarRequests = async () => {
        try {
            setLoading(true);
            const url = carId ? `/admin/car-requests?carId=${carId}` : '/admin/car-requests';
            const res = await axiosInstance.get(url);
            setRequests(res.data || []);
        } catch (error) {
            console.error('Error fetching car requests:', error);
            toast.error('Failed to load car requests');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCarRequests();
    }, [carId]);

    const handleUpdateStatus = async (id, newStatus) => {
        try {
            await axiosInstance.put(`/admin/car-requests/${id}/status`, { status: newStatus });
            toast.success(`Request status updated to ${newStatus}`);
            fetchCarRequests();
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error(error.response?.data?.message || 'Failed to update request status');
        }
    };

    const handleDeleteRequest = async (id) => {
        if (!window.confirm('Are you sure you want to delete this car request? This will permanently remove the request and linked messages.')) {
            return;
        }

        try {
            await axiosInstance.delete(`/admin/car-requests/${id}`);
            toast.success('Car request deleted successfully');
            fetchCarRequests();
        } catch (error) {
            console.error('Error deleting car request:', error);
            toast.error(error.response?.data?.message || 'Failed to delete car request');
        }
    };

    const formatDate = (val) => {
        if (!val) return '-';
        const dateObj = new Date(val);
        if (!isNaN(dateObj.getTime())) {
            const day = String(dateObj.getDate()).padStart(2, '0');
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const year = dateObj.getFullYear();
            return `${day}/${month}/${year}`;
        }
        return String(val);
    };

    const getRoleBadgeClass = (roleStr) => {
        if (!roleStr) return 'role-badge role-badge-lister';
        const r = roleStr.toLowerCase();
        if (r === 'business owner' || r === 'owner') return 'role-badge role-badge-owner';
        if (r === 'student') return 'role-badge role-badge-student';
        return 'role-badge role-badge-lister';
    };

    const getStatusBadge = (status) => {
        const s = String(status || 'pending').toLowerCase();
        if (s === 'accepted') {
            return (
                <span className="status-badge status-badge-accepted">
                    <FaCheckCircle size={11} /> Accepted
                </span>
            );
        }
        if (s === 'rejected') {
            return (
                <span className="status-badge status-badge-rejected">
                    <FaTimesCircle size={11} /> Rejected
                </span>
            );
        }
        return (
            <span className="status-badge status-badge-pending">
                <FaClock size={11} /> Pending
            </span>
        );
    };

    const filtered = requests.filter(r =>
        r.car_title?.toLowerCase().includes(search.toLowerCase()) ||
        r.requested_by?.toLowerCase().includes(search.toLowerCase()) ||
        r.listed_by?.toLowerCase().includes(search.toLowerCase()) ||
        r.email?.toLowerCase().includes(search.toLowerCase()) ||
        r.phone?.toLowerCase().includes(search.toLowerCase()) ||
        r.status?.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [search, carId]);

    const paginatedRequests = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const activeCarTitle = carId && requests.length > 0 ? requests[0].car_title : null;

    return (
        <div className="page-premium-wrapper cars-list-page">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header page-hero-properties">
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>

                    <div className="ph-left">
                        <div className="ph-icon" style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)' }}>
                            <FaClipboardList size={26} />
                        </div>
                        <div>
                            <h1 className="ph-title">Car Requests</h1>
                            <p className="ph-subtitle">
                                {carId ? `All requests for selected vehicle` : 'Manage all customer rental bookings & selling interest requests'}
                            </p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{requests.length}</span>
                            <span className="count-lbl">{carId ? 'Car Requests' : 'Total Requests'}</span>
                        </div>
                    </div>
                </div>

                {/* Filter Active Banner if carId specified */}
                {carId && (
                    <div className="alert alert-info d-flex align-items-center justify-content-between mb-4 border-0 shadow-sm rounded-3 px-4 py-3" style={{ backgroundColor: '#eff6ff', borderLeft: '4px solid #2563eb' }}>
                        <div className="d-flex align-items-center gap-2">
                            <FaFilter className="text-primary" />
                            <span>Showing request rows for Car ID <strong>#{carId}</strong> {activeCarTitle ? `(${activeCarTitle})` : ''}</span>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary fw-bold"
                            onClick={() => setSearchParams({})}
                        >
                            Show All Car Requests
                        </button>
                    </div>
                )}

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span>
                            {carId ? `Requests for Car #${carId}` : 'All Car Requests List'}
                        </div>
                        <div className="ptable-search">
                            <FaSearch className="ptable-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by car, user, lister, email..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="ptable-wrap">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}>#</th>
                                    <th style={{ minWidth: '220px' }}>Car Details</th>
                                    <th style={{ minWidth: '180px' }}>Requested By</th>
                                    <th style={{ minWidth: '140px' }}>Request Type</th>
                                    <th style={{ minWidth: '160px' }}>Listed By</th>
                                    <th style={{ minWidth: '150px' }}>Contact Info</th>
                                    <th style={{ minWidth: '110px' }}>Request Date</th>
                                    <th style={{ minWidth: '150px', whiteSpace: 'nowrap' }}>Status</th>
                                    <th className="text-center" style={{ minWidth: '140px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="9" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                            <p className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>Loading car requests...</p>
                                        </td>
                                    </tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="9">
                                            <div className="ptable-empty">
                                                <div className="ptable-empty-icon">📋</div>
                                                <p>No car requests found.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedRequests.map((req, idx) => {
                                        const isSelling = req.car_listing_type === 'selling' || req.car_listing_type === 'sale';
                                        let thumb = '';
                                        if (req.car_images && Array.isArray(req.car_images) && req.car_images.length > 0) {
                                            thumb = req.car_images[0];
                                        }

                                        return (
                                            <tr key={req.id}>
                                                <td style={{ color: '#9ba6c0', fontWeight: 600 }}>{(currentPage - 1) * pageSize + idx + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2.5">
                                                        {thumb ? (
                                                            <img
                                                                src={getFullImageUrl(thumb)}
                                                                alt="Car"
                                                                style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                                                            />
                                                        ) : (
                                                            <div className="d-flex align-items-center justify-content-center bg-light rounded text-muted" style={{ width: '48px', height: '48px' }}>
                                                                <FaCar size={20} />
                                                            </div>
                                                        )}
                                                        <div className="d-flex flex-column">
                                                            <Link to={`/cars/${req.car_id}`} style={{ fontWeight: 700, color: '#0f172a', textDecoration: 'none' }}>
                                                                {req.car_title}
                                                            </Link>
                                                            <span style={{ fontSize: '12px', color: '#64748b' }}>
                                                                ${req.car_price} {isSelling ? '(Sale)' : '/ day'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column gap-1">
                                                        <strong style={{ color: '#0f172a', fontSize: '0.9rem' }}>{req.requested_by}</strong>
                                                        <span className={getRoleBadgeClass(req.requester_type)} style={{ width: 'fit-content', fontSize: '11px', padding: '2px 8px' }}>
                                                            {req.requester_type}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`badge ${isSelling ? 'badge-type-selling' : 'badge-type-rental'} px-2.5 py-1`} style={{ fontSize: '12px', fontWeight: 600 }}>
                                                        {req.request_type}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column gap-1">
                                                        <strong style={{ color: '#334155', fontSize: '0.88rem' }}>{req.listed_by}</strong>
                                                        <span className={getRoleBadgeClass(req.listed_type)} style={{ width: 'fit-content', fontSize: '11px', padding: '2px 8px' }}>
                                                            {req.listed_type}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column" style={{ fontSize: '12px' }}>
                                                        <span style={{ color: '#0f172a', fontWeight: 500 }}>✉️ {req.email}</span>
                                                        <span style={{ color: '#64748b' }}>📞 {req.phone}</span>
                                                    </div>
                                                </td>
                                                <td style={{ color: '#475569', fontSize: '0.85rem', fontWeight: 500 }}>
                                                    {formatDate(req.request_date)}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    {getStatusBadge(req.status)}
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex justify-content-center align-items-center gap-1.5">
                                                        <select
                                                            className="form-select form-select-sm"
                                                            style={{ width: '100px', fontSize: '12px', borderRadius: '6px', borderColor: '#cbd5e1' }}
                                                            value={req.status || 'pending'}
                                                            onChange={(e) => handleUpdateStatus(req.id, e.target.value)}
                                                        >
                                                            <option value="pending">Pending</option>
                                                            <option value="accepted">Accepted</option>
                                                            <option value="rejected">Rejected</option>
                                                        </select>
                                                        <button
                                                            type="button"
                                                            className="action-btn action-btn-delete"
                                                            onClick={() => handleDeleteRequest(req.id)}
                                                            title="Delete Request"
                                                        >
                                                            <FaTrash size={12} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    <AdminPagination
                        totalItems={filtered.length}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(newSize) => { setPageSize(newSize); setCurrentPage(1); }}
                        itemLabel="car requests"
                    />
                </div>

            </div>
        </div>
    );
}
